import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { getFileContent } from "../../../api/Design/fileApi";

function PreviewImage({ filePath }) {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSrc(null);

    getFileContent(filePath)
      .then((response) => {
        if (cancelled) return;
        const file = response?.data;
        if (!file?.data || !file?.mimeType) {
          setError("No se pudo cargar la imagen");
          return;
        }
        setSrc(`data:${file.mimeType};base64,${file.data}`);
      })
      .catch(() => {
        if (!cancelled) setError("No se pudo cargar la imagen");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [filePath]);

  if (loading) {
    return (
      <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error || !src) {
    return (
      <Typography variant="body2" color="error" sx={{ py: 4 }}>
        {error}
      </Typography>
    );
  }

  return (
    <Box
      component="img"
      src={src}
      alt=""
      sx={{
        width: "100%",
        maxHeight: 420,
        objectFit: "contain",
        display: "block",
      }}
    />
  );
}

export default function PreparePhotosPreviewDialog({
  open,
  items = [],
  onClose,
}) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t("Samples.preparePhotosPreviewTitle")}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t("Samples.preparePhotosPreviewHint")}
        </Typography>
        <Typography
          component="ul"
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, pl: 2.5, m: 0 }}
        >
          <li>{t("Samples.preparePhotosPreviewStepPdf")}</li>
          <li>{t("Samples.preparePhotosPreviewStepReopen")}</li>
          <li>{t("Samples.preparePhotosPreviewStepOriginals")}</li>
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            justifyContent: "center",
          }}
        >
          {items.map((item) => (
            <Box
              key={item.path}
              sx={{
                textAlign: "center",
                width: { xs: "100%", sm: 320 },
              }}
            >
              <Chip
                size="small"
                label={
                  item.role === "front"
                    ? t("Samples.pdfPhotoFront")
                    : item.role === "back"
                      ? t("Samples.pdfPhotoBack")
                      : t("Samples.cleanPhotoBadge")
                }
                color="primary"
                sx={{ mb: 1 }}
              />
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 1,
                  bgcolor: "#fff",
                  minHeight: 280,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PreviewImage filePath={item.path} />
              </Box>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="contained" onClick={onClose}>
          {t("Samples.preparePhotosPreviewDone")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
